// OtterBonk's hit feel (打击感), drawn by client.js from what the snapshots say (EV_DMG: who hit what for how much, a
// crit, a kill; an enemy's flash; my hurt flag). Nothing here touches the game: it's all on the page.
//   - hitstop: the drawn world holds still for a few tens of ms on a heavy blow (a 大石锤 landing, a 回旋斩, a crit, an
//     elite or a boss going down), then catches up a little faster than real time (lag: ms the world is drawn behind
//     where it would be; it goes back to 0). Never more often than HITSTOP_GAP, never longer than HITSTOP_MAX.
//   - screen shake: trauma (0..1) added by hit weight, the offset its square times the setting's px, decaying; the
//     setting (localStorage ob.shake: 2 high / 1 low / 0 off; off by default under prefers-reduced-motion) scales or stops it.
//   - particles: one ring buffer of typed arrays (no allocation per hit), oldest overwritten when full; each a square
//     flying out with a little arc (z, gravity) and fading. LITE (省电模式) halves what each burst makes.
//   - damage numbers: pooled DOM, moved by transform only; they pop, fly out in an arc and fade; hits on one enemy close
//     together add into one number (it pops again, a little bigger), and past the cap the oldest makes way.

export const REDUCED_MOTION = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
/** The screen-shake setting: 2 high, 1 low, 0 off. */
export function shakeSetting() {
  let v = null; try { v = localStorage.getItem('ob.shake'); } catch {}
  return v === '0' || v === '1' || v === '2' ? +v : REDUCED_MOTION ? 0 : 2;
}
export function setShakeSetting(v) { try { localStorage.setItem('ob.shake', String(v)); } catch {} }
/** The settings' words for each level (locales: ob.shake.*). */
export const SHAKE_KEYS = ['ob.shake.off', 'ob.shake.low', 'ob.shake.high'];

const HITSTOP_MAX = 60, HITSTOP_GAP = 220, LAG_MAX = 120, SHAKE_PX = [0, 2, 4], TRAUMA_DECAY = 1.8; // ms, ms, ms, world px, per s
const P_MAX = 240, P_LITE = 100;
const NUM_T = 700, NUM_CRIT_T = 950, NUM_MERGE = 400, NUM_MAX = 12, NUM_LITE = 6;

export class Juice {
  constructor(lite) {
    this.lite = lite; this.level = shakeSetting();
    this.stop = 0; this.lag = 0; this.lastStop = -1e9; this.trauma = 0; this.ox = 0; this.oy = 0;
    const n = P_MAX;
    this.px = new Float32Array(n); this.py = new Float32Array(n); this.pz = new Float32Array(n); this.vx = new Float32Array(n); this.vy = new Float32Array(n); this.vz = new Float32Array(n);
    this.life = new Float32Array(n); this.max = new Float32Array(n); this.col = new Uint32Array(n); this.size = new Uint8Array(n); this.pn = 0; this.pi = 0;
    this.nums = []; this.hurtAt = -1e9; this.hurtA = 0;
  }
  reset() { this.stop = 0; this.lag = 0; this.trauma = 0; this.ox = this.oy = 0; this.life.fill(0); this.nums.length = 0; this.hurtAt = -1e9; }
  setLevel(v) { this.level = v; if (!v) { this.trauma = 0; this.ox = this.oy = 0; } }
  /** Hold the drawn world still for ms (a heavy blow), at most every gap ms (HITSTOP_GAP; a crit's longer: they come often). */
  hitstop(ms, now, gap = HITSTOP_GAP) {
    if (now - this.lastStop < gap || this.stop > 0) return;
    this.lastStop = now; this.stop = Math.min(HITSTOP_MAX, ms);
  }
  shake(w) { if (this.level) this.trauma = Math.min(1, this.trauma + w); }
  hurt(now, a = 1) { this.hurtAt = now; this.hurtA = Math.min(1, a); }
  /** Once a frame: the hitstop's hold and catch-up, the shake's offset, the particles moving. */
  tick(frame, now, paused = false) {
    if (this.stop > 0) { const d = Math.min(frame, this.stop); this.stop -= d; this.lag = Math.min(LAG_MAX, this.lag + d); }
    else if (this.lag > 0) this.lag = Math.max(0, this.lag - frame * 0.5);
    // Simulation time stands still while paused, but `now` does not. Do not leave a non-decaying shake oscillating
    // under the upgrade or pause overlay, or bring an old jolt back when play resumes.
    if (paused) { this.trauma = 0; this.ox = this.oy = 0; }
    else if (this.trauma > 0 && this.level) {
      this.trauma = Math.max(0, this.trauma - (TRAUMA_DECAY * frame) / 1000);
      const a = SHAKE_PX[this.level] * this.trauma * this.trauma, t = now / 1000;
      this.ox = Math.round(a * (Math.sin(t * 71) * 0.6 + Math.sin(t * 113 + 1.3) * 0.4));
      this.oy = Math.round(a * (Math.sin(t * 89 + 2.1) * 0.6 + Math.sin(t * 131 + 0.4) * 0.4));
    } else { this.ox = this.oy = 0; }
    const dt = this.stop > 0 ? 0 : frame / 1000; // (held with the world)
    if (!dt) return;
    const L = this.life, n = P_MAX;
    for (let i = 0; i < n; i++) {
      if (L[i] <= 0) continue;
      L[i] -= dt;
      this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt; this.vx[i] *= 1 - 3 * dt; this.vy[i] *= 1 - 3 * dt;
      this.vz[i] -= 260 * dt; this.pz[i] = Math.max(0, this.pz[i] + this.vz[i] * dt);
    }
  }
  /** One particle (world px, px/s; z up). */
  part(x, y, vx, vy, vz, life, col, size) {
    const cap = this.lite.on ? P_LITE : P_MAX, i = this.pi; this.pi = (this.pi + 1) % cap;
    this.px[i] = x; this.py[i] = y; this.pz[i] = 0; this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz; this.life[i] = this.max[i] = life; this.col[i] = col; this.size[i] = size;
  }
  /** A burst of n bits out of (x, y) at about speed px/s, in col (the second colour for every other one). */
  burst(x, y, n, speed, col, col2 = col, size = 2, life = 0.35) {
    n = Math.max(1, Math.round(n * (this.lite.on ? 0.5 : 1)));
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + Math.random() * 0.6, s = speed * (0.55 + Math.random() * 0.6);
      this.part(x, y, Math.cos(a) * s, Math.sin(a) * s * 0.7, 40 + Math.random() * 70, life * (0.75 + Math.random() * 0.5), k % 2 ? col2 : col, size);
    }
  }
  /** Sparks off a hit at (x, y): flying away from the hitter (angle: from it to the enemy), a narrow fan. */
  sparks(x, y, angle, n, col, speed = 110, size = 1) {
    n = Math.max(1, Math.round(n * (this.lite.on ? 0.5 : 1)));
    for (let k = 0; k < n; k++) {
      const a = angle + (Math.random() - 0.5) * 1.6, s = speed * (0.6 + Math.random() * 0.7);
      this.part(x, y, Math.cos(a) * s, Math.sin(a) * s * 0.75, 20 + Math.random() * 50, 0.18 + Math.random() * 0.12, col, size + (k === 0 ? 1 : 0));
    }
  }
  drawParticles(g) {
    const L = this.life;
    for (let i = 0; i < P_MAX; i++) {
      if (L[i] <= 0) continue;
      const k = L[i] / this.max[i], s = this.size[i] + (k > 0.6 ? 1 : 0);
      g.fillStyle(this.col[i], Math.min(1, k * 1.6));
      g.fillRect(Math.round(this.px[i] - s / 2), Math.round(this.py[i] - this.pz[i] - s / 2), s, s);
    }
  }
  /** A damage number for a hit of mine on `id` at (x, y). */
  number(id, x, y, dmg, crit, now) {
    const N = this.nums, cap = this.lite.on ? NUM_LITE : NUM_MAX;
    for (let i = N.length - 1; i >= 0; i--) { // the same enemy just now: one number, adding up
      const q = N[i]; if (q.id !== id || now - q.last > NUM_MERGE) continue;
      q.dmg += dmg; q.crit ||= crit; q.last = now; q.pop = now; return;
    }
    // too many at once: the oldest goes (a crit stays over a plain one), so the newest hits always read
    if (N.length >= cap) { const o = N.findIndex((q) => !q.crit); N.splice(o < 0 ? 0 : o, 1); }
    N.push({ id, x: x + (Math.random() - 0.5) * 6, y: y + (Math.random() - 0.5) * 6, dmg, crit, at: now, last: now, pop: now, vx: (Math.random() < 0.5 ? -1 : 1) * (14 + Math.random() * 18) });
  }
  /** The numbers over the arena (DOM in box, view: the camera's world view, K CSS px a world px). */
  drawNumbers(box, v, K, now) {
    const N = this.nums; let j = 0;
    for (let i = 0; i < N.length; i++) { const q = N[i]; if (now - q.last < (q.crit ? NUM_CRIT_T : NUM_T)) N[j++] = q; }
    N.length = j;
    const els = box.children;
    for (let i = 0; i < N.length; i++) {
      const q = N[i], t = (now - q.at) / 1000, life = (q.crit ? NUM_CRIT_T : NUM_T), k = (now - q.last) / life;
      const d = els[i] ?? box.appendChild(Object.assign(document.createElement('div'), { className: 'obdmg' }));
      const txt = String(Math.max(1, Math.round(q.dmg))); if (d.textContent !== txt) d.textContent = txt;
      const cls = 'obdmg' + (q.crit ? ' crit' : q.last > q.at || q.dmg >= 100 ? ' big' : ''); if (d.className !== cls) d.className = cls;
      const tt = Math.min(t, 0.9), wx = q.x + q.vx * tt, wy = q.y - 10 - (48 * tt - 50 * tt * tt);
      const pk = (now - q.pop) / 90, s = (pk < 1 ? 1 + (q.crit ? 0.9 : 0.6) * (1 - pk) : 1) * (q.crit ? 1.15 : 1);
      d.style.transform = `translate3d(${Math.round((wx - v.x) * K)}px,${Math.round((wy - v.y) * K)}px,0) translate(-50%,-100%) scale(${s.toFixed(2)})`;
      d.style.opacity = k < 0.7 ? '1' : ((1 - k) / 0.3).toFixed(2); d.style.display = '';
    }
    for (let i = N.length; i < els.length; i++) if (els[i].style.display !== 'none') els[i].style.display = 'none';
  }
  /** The red edge when I'm hurt (el: the vignette), fading over 400 ms. */
  drawHurt(el, now) {
    const k = 1 - (now - this.hurtAt) / 400, a = k > 0 ? (0.45 + 0.55 * this.hurtA) * k : 0, s = a.toFixed(2);
    if (el.style.opacity !== s && !(a === 0 && el.style.opacity === '0')) el.style.opacity = s;
  }
}
